export function initProductSliders(): void {
  document.querySelectorAll<HTMLElement>('[data-products-slider]').forEach((slider) => {
    const track = slider.querySelector<HTMLElement>('[data-products-track]');
    const previous = slider.querySelector<HTMLButtonElement>('[data-products-previous]');
    const next = slider.querySelector<HTMLButtonElement>('[data-products-next]');

    if (!track || !previous || !next) {
      return;
    }

    const move = (direction: number): void => {
      const card = track.querySelector<HTMLElement>('.products__card');
      const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
      const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth;

      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
  });
}

export function initProductFilters(): void {
  document.querySelectorAll<HTMLElement>('[data-products-filter]').forEach((filterSection) => {
    const buttons = filterSection.querySelectorAll<HTMLButtonElement>('[data-products-filter-button]');
    const cards = filterSection.querySelectorAll<HTMLElement>('[data-products-filter-card]');
    const track = filterSection.querySelector<HTMLElement>('[data-products-track]');

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const category = button.dataset.productsFilterButton ?? '';

        buttons.forEach((filterButton) => {
          filterButton.setAttribute('aria-pressed', filterButton === button ? 'true' : 'false');
        });

        cards.forEach((card) => {
          card.hidden = category !== '' && card.dataset.productsCategory !== category;
        });

        track?.scrollTo({ left: 0 });
        filterSection.dispatchEvent(new CustomEvent('products:filterchange'));
      });
    });
  });
}

export function initProductGridLoadMore(): void {
  document.querySelectorAll<HTMLElement>('[data-products-load-more-grid]').forEach((grid) => {
    const section = grid.closest<HTMLElement>('.section--products');
    const button = section?.querySelector<HTMLButtonElement>('[data-products-load-more]');
    const cards = Array.from(grid.querySelectorAll<HTMLElement>('[data-products-load-more-card]'));
    const step = button ? Number.parseInt(button.dataset.productsLoadMoreStep ?? '6', 10) : 6;
    const pageSize = Number.isFinite(step) && step > 0 ? step : 6;

    if (!section || !button || cards.length <= pageSize) {
      return;
    }

    let visibleCount = pageSize;

    const updateCards = (): void => {
      const filterVisibleCards = cards.filter((card) => !card.hidden);

      cards.forEach((card) => {
        const filterVisibleIndex = filterVisibleCards.indexOf(card);
        const shouldShow = filterVisibleIndex >= 0 && filterVisibleIndex < visibleCount;

        card.classList.toggle('is-load-more-hidden', !shouldShow);
      });

      button.hidden = filterVisibleCards.length <= visibleCount;
    };

    button.addEventListener('click', () => {
      visibleCount += pageSize;
      updateCards();
    });

    section.addEventListener('products:filterchange', () => {
      visibleCount = pageSize;
      updateCards();
    });

    updateCards();
  });
}

export function initProductLightboxes(): void {
  const triggers = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-products-lightbox-open]'));

  if (triggers.length === 0) {
    return;
  }

  const overlay = document.createElement('div');
  const closeButton = document.createElement('button');
  const figure = document.createElement('figure');
  const stage = document.createElement('div');
  const caption = document.createElement('figcaption');

  overlay.className = 'products__lightbox';
  overlay.hidden = true;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Produktbild');
  overlay.dataset.productsLightboxOverlay = '';

  closeButton.className = 'products__lightbox-close';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Bildansicht schliessen');
  closeButton.textContent = '×';

  figure.className = 'products__lightbox-stage';
  stage.className = 'products__lightbox-media';
  caption.className = 'products__lightbox-caption';

  figure.append(stage, caption);
  overlay.append(closeButton, figure);
  document.body.append(overlay);

  let previouslyFocused: HTMLElement | null = null;

  const close = (): void => {
    overlay.hidden = true;
    document.documentElement.classList.remove('has-overlay');
    stage.replaceChildren();
    caption.textContent = '';
    previouslyFocused?.focus();
  };

  const open = (trigger: HTMLAnchorElement): void => {
    const imageSrc = trigger.href;

    if (!imageSrc) {
      return;
    }

    const image = document.createElement('img');
    image.src = imageSrc;
    image.alt = trigger.dataset.productsLightboxAlt ?? '';

    previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stage.replaceChildren(image);
    caption.textContent = trigger.dataset.productsLightboxCaption ?? '';
    overlay.hidden = false;
    document.documentElement.classList.add('has-overlay');
    closeButton.focus();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open(trigger);
    });
  });

  closeButton.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!overlay.hidden && event.key === 'Escape') {
      close();
    }
  });
}
